import { Box, Skeleton } from '@mui/material';
import { ReactNode, useRef } from 'react';
import { useInView } from '../../hooks/useInView';

export type DeferMountWhenVisibleProps = {
    children: ReactNode;
    /** Reserved vertical space before the section mounts (reduces layout shift) */
    minHeight?: number;
    /** Passed to IntersectionObserver — positive margin prefetches before scroll reaches the block */
    rootMargin?: string;
    /** When false, renders an empty Box with minHeight instead of a Skeleton */
    showSkeleton?: boolean;
};

/**
 * Mounts `children` only after the wrapper intersects the viewport so hooks inside
 * children (e.g. react-query) run in a staggered way instead of all on first paint.
 */
const DeferMountWhenVisible = ({
    children,
    minHeight = 280,
    rootMargin = '160px 0px',
    showSkeleton = true,
}: DeferMountWhenVisibleProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const inView = useInView(containerRef, { rootMargin, triggerOnce: true });

    return (
        <div ref={containerRef}>
            {inView ? (
                children
            ) : showSkeleton ? (
                <Skeleton variant="rounded" animation="wave" sx={{ minHeight, borderRadius: 1 }} />
            ) : (
                <Box sx={{ minHeight }} />
            )}
        </div>
    );
};

export default DeferMountWhenVisible;
